import React from 'react';
import { useNavigate } from 'react-router-dom';
import { PageLayout } from '@/shared/layout';
import { Button, Filter, Search, KPICard, Card, Badge, Table, Avatar } from '@/components/ui';
import { useDesignDocuments } from './hooks/useDesignDocuments';
import { useAuth } from '@/core/auth/AuthContext';
import { AuthButton } from '@/core/permissions/AuthButton';
import type { DesignDocumentStatus, DesignDocumentType } from './types/design-documents.types';

const statusColors: Record<DesignDocumentStatus, string> = {
  draft: 'bg-slate-100 text-slate-600',
  submitted: 'bg-blue-50 text-blue-500',
  under_review: 'bg-amber-100 text-amber-600',
  approved: 'bg-emerald-100 text-emerald-600',
  approved_with_comments: 'bg-indigo-100 text-indigo-600',
  rejected: 'bg-red-100 text-red-600',
  superseded: 'bg-slate-400 text-white',
  obsolete: 'bg-slate-900 text-white',
  for_construction: 'bg-os-primary text-white',
  as_built: 'bg-teal-600 text-white',
};

const statusLabels: Record<DesignDocumentStatus, string> = {
  draft: 'Bản thảo',
  submitted: 'Đã nộp',
  under_review: 'Đang soát xét',
  approved: 'Đã duyệt',
  approved_with_comments: 'Duyệt kèm ý kiến',
  rejected: 'Từ chối',
  superseded: 'Đã thay thế',
  obsolete: 'Hết hiệu lực',
  for_construction: 'Dùng thi công',
  as_built: 'Hoàn công',
};

const typeLabels: Record<DesignDocumentType, string> = {
  landscape_master_plan: 'Tổng mặt bằng',
  planting_plan: 'Bản vẽ Cây xanh',
  irrigation_plan: 'Bản vẽ Tưới',
  lighting_plan: 'Bản vẽ Đèn',
  hardscape_plan: 'Bản vẽ Hardscape',
  grading_plan: 'Bản vẽ San nền',
  shop_drawing: 'Shop Drawing',
  as_built_drawing: 'Bản vẽ Hoàn công',
  specification: 'Spec / Quy cách',
  method_statement: 'Biện pháp thi công',
  material_approval: 'Phê duyệt vật liệu',
  sample_approval: 'Phê duyệt mẫu',
  rfi_response: 'Phản hồi RFI',
  design_change: 'Thay đổi thiết kế',
};

export default function DesignDocumentListPage() {
  const { profile } = useAuth();
  const companyId = profile?.company_id || '';
  const navigate = useNavigate();

  const {
    documents,
    stats,
    isLoading,
    filters,
    setFilters,
  } = useDesignDocuments(companyId);

  return (
    <PageLayout
      title="Hồ sơ Thiết kế (Design Documents)"
      description="Trung tâm lưu trữ và phê duyệt bản vẽ kỹ thuật, biện pháp thi công và hồ sơ phê duyệt vật liệu."
      breadcrumbs={[{ label: 'Dự án' }, { label: 'Hồ sơ thiết kế' }]}
      isLoading={isLoading}
      headerActions={
        <div className="flex gap-2">
          <Button variant="outline" icon="ri-history-line">Lịch sử thay đổi</Button>
          <AuthButton
            module="projects"
            action="create"
            variant="primary"
            icon="ri-upload-cloud-2-line"
            onClick={() => navigate('./upload')}
          >
            Tải lên hồ sơ
          </AuthButton>
        </div>
      }
    >
      <div className="space-y-6">
        {/* KPI Board */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <KPICard
            title="Tổng hồ sơ"
            value={stats?.totalDocuments || 0}
            icon="ri-folder-zip-line"
            variant="primary"
          />
          <KPICard
            title="Bản vẽ thi công (IFC)"
            value={stats?.forConstructionCount || 0}
            icon="ri-tools-line"
            variant="success"
          />
          <KPICard
            title="Đang soát xét"
            value={stats?.underReviewCount || 0}
            icon="ri-eye-line"
            variant="warning"
          />
          <KPICard
            title="Số phiên bản (Revisions)"
            value={stats?.latestRevisionsCount || 0}
            icon="ri-git-repository-commits-line"
            variant="info"
          />
        </div>

        <PageLayout.Toolbar>
          <div className="flex flex-col md:flex-row gap-4 w-full justify-between items-start md:items-center">
            <div className="flex flex-wrap gap-3 flex-1">
              <Search
                placeholder="Tìm mã hồ sơ, tên bản vẽ..."
                value={filters.search}
                onSearch={(v) => setFilters({ ...filters, search: v })}
                className="w-full md:w-80"
              />
              <Filter
                options={[
                  {
                    id: 'status', label: 'Trạng thái', type: 'select', choices: [
                      { value: 'all', label: 'Tất cả trạng thái' },
                      ...Object.entries(statusLabels).map(([val, label]) => ({ value: val, label }))
                    ]
                  },
                  {
                    id: 'type', label: 'Loại hồ sơ', type: 'select', choices: [
                      { value: 'all', label: 'Tất cả loại' },
                      ...Object.entries(typeLabels).map(([val, label]) => ({ value: val, label }))
                    ]
                  }
                ]}
                onFilterChange={(k, v) => setFilters({ ...filters, [k]: v })}
              />
            </div>
          </div>
        </PageLayout.Toolbar>

        <Card className="overflow-hidden border-none shadow-sm">
          <Table
            columns={[
              { key: 'code', title: 'Mã hồ sơ' },
              { key: 'title', title: 'Tên hồ sơ & Phân loại' },
              { key: 'rev', title: 'Rev', align: 'center' },
              { key: 'location', title: 'Định vị' },
              { key: 'status', title: 'Trạng thái', align: 'center' },
              { key: 'uploader', title: 'Người nộp' },
            ]}
            data={documents}
            isLoading={isLoading}
            onRowClick={(item) => navigate(`./detail/${item.id}`)}
            renderCell={(item, key) => {
              switch (key) {
                case 'code':
                  return <span className="font-mono text-[11px] font-bold text-os-primary tracking-wider">{item.document_code}</span>;
                case 'title':
                  return (
                    <div className="flex flex-col">
                      <div className="flex items-center gap-2">
                        <i className={`ri-${item.mime_type?.includes('pdf') ? 'file-pdf' : 'image'}-line text-slate-400`} />
                        <span className="font-bold text-slate-900 line-clamp-1">{item.title}</span>
                      </div>
                      <Badge variant="neutral" className="bg-slate-50 text-slate-400 text-[9px] font-bold w-fit mt-0.5">
                        {typeLabels[item.document_type]}
                      </Badge>
                    </div>
                  );
                case 'rev':
                  return <Badge variant="neutral" className="bg-slate-100 text-slate-600 font-bold text-[10px]">R{item.revision_no}</Badge>;
                case 'location':
                  return (
                    <div className="flex flex-col">
                      <span className="sm:text-sm text-xs font-normal text-slate-700">{item.project?.name}</span>
                      <span className="text-[10px] text-slate-400">{item.zone?.name || 'Toàn dự án'}</span>
                    </div>
                  );
                case 'status':
                  return (
                    <Badge className={`${statusColors[item.status_code]} border-transparent text-[10px] font-bold`}>
                      {statusLabels[item.status_code]}
                    </Badge>
                  );
                case 'uploader':
                  return (
                    <div className="flex items-center gap-2">
                      <Avatar name={item.uploader?.full_name} size="xs" />
                      <div className="flex flex-col">
                        <span className="text-[10px] font-bold text-slate-700">{item.uploader?.full_name || '---'}</span>
                        <span className="text-[9px] text-slate-400">{item.submitted_at ? new Date(item.submitted_at).toLocaleDateString('vi-VN') : '---'}</span>
                      </div>
                    </div>
                  );
                default:
                  return null;
              }
            }}
          />
        </Card>
      </div>
    </PageLayout>
  );
}
